import { parse as parseBestEffortJsonStream } from "best-effort-json-parser";
import { usePostHog } from "posthog-js/react";
import type React from "react";
import { cloneElement, useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";

import type { CopilotRequest, PostTemplateDto } from "@/api/types";
import { ConfirmModal } from "@/components/ConfirmModal/ConfirmModal";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { FormRadioCardGroup } from "@/components/Form/FormRadioCard";
import { Asterisk01Icon } from "@/components/Icons/Asterisk01Icon";
import { Building04Icon } from "@/components/Icons/Building04Icon";
import { Edit05Icon } from "@/components/Icons/Edit05Icon";
import { MagicWand02Icon } from "@/components/Icons/MagicWand02Icon";
import { Tool01Icon } from "@/components/Icons/Tool01Icon";
import { Users01Icon } from "@/components/Icons/Users01Icon";
import { Modal, type ModalBaseProps } from "@/components/Modal/Modal";
import { MultiLineContent } from "@/components/MultiLineContent/MultiLineContent";
import { RadioCard } from "@/components/Radio/RadioCard";
import { SearchInput } from "@/components/SearchInput/SearchInput";
import { Button } from "@/design-system/Button";
import { Card } from "@/design-system/Card";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Inline } from "@/design-system/Inline";
import { SectionCard } from "@/design-system/SectionCard";
import { Stack } from "@/design-system/Stack";
import { Tabs } from "@/design-system/Tabs";
import { Tiles } from "@/design-system/Tiles";
import { isAbortError } from "@/helpers/isAbortError";
import { processJsonStream } from "@/helpers/processJsonStream";
import { useBool } from "@/hooks/useBool";
import { useDebounceValue } from "@/hooks/useDebounce";
import { useMutationAbortController } from "@/hooks/useMutationAbortController";
import { generalMutations } from "@/queries/general/mutations";
import { useTranslation } from "@/translations";

import type { AdminCreatePostSharedFormValues } from "../AdminCreatePostPage";

enum TemplateSelectorModalState {
  BROWSE,
  PREVIEW,
}
type ToneOfVoice = Exclude<CopilotRequest["toneOfVoice"], undefined>;
type AdminCreatePostFormTemplateSelectorProps = {
  templates: PostTemplateDto[];
  onApplyTemplate: ({
    template,
    title,
    content,
  }: {
    template: PostTemplateDto;
    title: string;
    content: string;
  }) => void;
};

const DEFAULT_TONE_OF_VOICE: Exclude<CopilotRequest["toneOfVoice"], undefined> = "neutral";

export function AdminCreatePostFormTemplateSelector({
  templates,
  onApplyTemplate,
}: AdminCreatePostFormTemplateSelectorProps): React.ReactNode {
  const [isModalOpened, modalHandler] = useBool();

  const { t } = useTranslation();
  const postHog = usePostHog();

  const handleClickBrowse = () => {
    postHog.capture("clicked_browse_post_templates");

    modalHandler.setTrue();
  };

  return (
    <>
      <SectionCard icon={<MagicWand02Icon />} title={t("page.admin-create-post.section.templates.title")}>
        <section className="flex w-full flex-col flex-wrap items-start justify-between gap-x-6 gap-y-4 @sm:flex-row @sm:items-center">
          <p className="flex-1 whitespace-pre-wrap @sm:min-w-search-wide">
            {t("page.admin-create-post.section.templates.description")}
          </p>
          <Button data-testid="browse-template-btn" variant="soft" onClick={handleClickBrowse}>
            {t("page.admin-create-post.section.templates.btn.browse")}
          </Button>
        </section>
      </SectionCard>
      <TemplateSelectorModal
        isOpened={isModalOpened}
        onOpenChange={modalHandler.set}
        templates={templates}
        onSelectTemplate={({ template, generatedTitle, generatedContent }) =>
          onApplyTemplate({ template, title: generatedTitle, content: generatedContent })
        }
      />
    </>
  );
}

type TemplateSelectorModalProps = ModalBaseProps & {
  templates: PostTemplateDto[];
  onSelectTemplate: ({
    template,
    generatedTitle,
    generatedContent,
  }: {
    template: PostTemplateDto;
    generatedTitle: string;
    generatedContent: string;
  }) => void;
};

function TemplateSelectorModal({
  templates,
  isOpened,
  onOpenChange,
  onSelectTemplate,
}: TemplateSelectorModalProps): React.ReactNode {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [selectedToneOfVoice, setSelectedToneOfVoice] = useState<ToneOfVoice>(DEFAULT_TONE_OF_VOICE);
  const [previewTemplate, setPreviewTemplate] = useState<PostTemplateDto | undefined>(undefined);
  const [templateDetails, setTemplateDetails] = useState({
    title: "",
    content: "",
  });
  const [isOverwriteConfirmModalOpened, overwriteConfirmModalHandler] = useBool(false);
  const templateGroupByCategory = templates.reduce<Record<string, PostTemplateDto[]>>(
    (acc, template) => {
      if (!acc[template.category]) {
        acc[template.category] = [];
      }
      acc["ALL"].push(template);
      acc[template.category].push(template);

      return acc;
    },
    { ALL: [] },
  );
  const templateCategories = Object.keys(templateGroupByCategory).toSorted((a, b) => {
    return a.localeCompare(b);
  });
  const defaultActiveTab = templateCategories[0];
  const [activeTab, setActiveTab] = useState<string>(defaultActiveTab);

  const { t } = useTranslation();
  const postHog = usePostHog();
  const showFlashToast = useFlashToast();
  const form = useFormContext<AdminCreatePostSharedFormValues>();
  const debouncedQuery = useDebounceValue(searchQuery.toLowerCase().trim(), 300);
  const searchedTemplates = useMemo(
    () => templates.filter((template) => template.name.toLowerCase().includes(debouncedQuery.toLowerCase())),
    [debouncedQuery, templates],
  );
  const abortController = useMutationAbortController();
  const generateTemplateDetailsMutation = generalMutations.useGetCopilotResponse();
  const isOverwrittingConfirmationRequired = form.formState.dirtyFields.title || form.formState.dirtyFields.content;

  useEffect(() => {
    // Reset modal state on open
    if (isOpened) {
      // oxlint-disable-next-line react-hooks/set-state-in-effect
      setPreviewTemplate(undefined);
      setSelectedToneOfVoice(DEFAULT_TONE_OF_VOICE);
      setActiveTab(defaultActiveTab);
      setSearchQuery("");
      setTemplateDetails({
        title: "",
        content: "",
      });
    }
  }, [defaultActiveTab, isOpened]);

  useEffect(() => {
    if (debouncedQuery && searchedTemplates.length === 0) {
      postHog.capture("searched_missing_templates", {
        query: debouncedQuery,
      });
    }
  }, [postHog, debouncedQuery, searchedTemplates.length]);

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case "ALL":
        return t("component.post-template-selector.category.label.all");
      case "COMMUNITY":
        return t("component.post-template-selector.category.label.community");
      case "MAINTENANCE":
        return t("component.post-template-selector.category.label.maintenance");
      case "AMENITIES":
        return t("component.post-template-selector.category.label.amenities");
      case "SAFETY":
        return t("component.post-template-selector.category.label.safety");
      case "RULES":
        return t("component.post-template-selector.category.label.rules");
      default:
        return category;
    }
  };

  const generateTemplateDetails = useCallback(
    async ({ template, toneOfVoice }: { template: PostTemplateDto; toneOfVoice: ToneOfVoice }) => {
      setIsGenerating(true);

      let title = "";
      let content = "";
      try {
        const response = await generateTemplateDetailsMutation.mutateAsync({
          templateId: template.id,
          userInput: template.prompt,
          toneOfVoice: toneOfVoice,
          copilotOptions: [],
          signal: abortController.getSignal(),
        });

        if (!response.ok || !response.body) {
          throw new Error("Failed to generate template details");
        }

        let buffer = "";
        await processJsonStream<{ chunk: string }>({
          stream: response.body,
          callbacks: {
            onChunk: (result) => {
              buffer += result.chunk;

              const currData = parseBestEffortJsonStream(buffer) as { title?: string; content?: string };

              if (currData.title && currData.content) {
                setTemplateDetails({
                  title: currData.title || "",
                  content: currData.content || "",
                });
              }
            },
          },
        });

        const data = parseBestEffortJsonStream(buffer) as { title?: string; content?: string };

        title = data.title || "";
        content = data.content || "";
      } catch (error) {
        if (!isAbortError(error)) {
          showFlashToast({
            message: t("page.admin-create-post.template-selector.error.template-generation"),
            variant: "alert",
          });
        }
      }

      setIsGenerating(false);

      return {
        title,
        content,
      };
    },
    [generateTemplateDetailsMutation, showFlashToast, t, abortController],
  );

  // Mark placeholder values (e.g. "[DATE]") as bold
  const formatTemplateContent = (content: string) => {
    const parts = [];
    const regex = /\[([^\]]+)\]/g;

    let lastIndex = 0;
    let match;
    let key = 0;
    while ((match = regex.exec(content)) !== null) {
      // Add text without any placeholder before the current placeholder
      if (match.index > lastIndex) {
        parts.push(content.slice(lastIndex, match.index));
      }

      // Add the current placeholder
      parts.push(
        <b className="text-caption-emphasis" key={key++}>
          [{match[1]}]
        </b>,
      );
      lastIndex = regex.lastIndex;
    }

    // Add remaining text without any placeholder
    if (lastIndex < content.length) {
      parts.push(content.slice(lastIndex));
    }

    return parts;
  };

  const getModalTitle = () => {
    switch (modalState) {
      case TemplateSelectorModalState.BROWSE:
        return t("page.admin-create-post.template-selector-modal.browse.title");
      case TemplateSelectorModalState.PREVIEW:
        return t("page.admin-create-post.template-selector-modal.preview.title", {
          templateName: previewTemplate?.name || "",
        });
      default:
        return "";
    }
  };

  const getModalDescription = () => {
    switch (modalState) {
      case TemplateSelectorModalState.BROWSE:
        return t("page.admin-create-post.template-selector-modal.browse.description");
      case TemplateSelectorModalState.PREVIEW:
        return t("page.admin-create-post.template-selector-modal.preview.description");
      default:
        return "";
    }
  };

  // If a template is selected after it's being previewed,
  // we don't need to generate it again
  const handleSelectPreviewedTemplate = () => {
    if (!previewTemplate) {
      return;
    }

    if (isOverwrittingConfirmationRequired) {
      overwriteConfirmModalHandler.setTrue();
    } else {
      onSelectTemplate({
        template: previewTemplate,
        generatedTitle: templateDetails.title,
        generatedContent: templateDetails.content,
      });
      onOpenChange(false);
    }
  };

  const handleSelectTemplateWithOverwrite = () => {
    if (!previewTemplate) {
      return;
    }

    onSelectTemplate({
      template: previewTemplate,
      generatedTitle: templateDetails.title,
      generatedContent: templateDetails.content,
    });

    overwriteConfirmModalHandler.setFalse();
    onOpenChange(false);
  };

  const handleSelectPreviewTemplate = async (template: PostTemplateDto) => {
    setPreviewTemplate(template);

    postHog.capture("clicked_preview_post_templates", {
      templateId: template.id,
      templateName: template.name,
    });

    if (template) {
      await generateTemplateDetails({
        template,
        toneOfVoice: selectedToneOfVoice,
      });
    }
  };

  const handleDeselectPreviewTemplate = () => {
    setPreviewTemplate(undefined);
    setSelectedToneOfVoice(DEFAULT_TONE_OF_VOICE);
    setTemplateDetails({
      title: "",
      content: "",
    });
  };

  const handleChangeToneOfVoice = async (toneOfVoice: ToneOfVoice) => {
    if (!previewTemplate) {
      return;
    }

    setSelectedToneOfVoice(toneOfVoice);

    await generateTemplateDetails({
      template: previewTemplate,
      toneOfVoice: toneOfVoice,
    });
  };

  const handleSelectNoTemplate = () => {
    postHog.capture("clicked_create_blank_post");

    onOpenChange(false);
  };

  const modalState: TemplateSelectorModalState = previewTemplate
    ? TemplateSelectorModalState.PREVIEW
    : TemplateSelectorModalState.BROWSE;

  return (
    <>
      <Modal.Root
        data-testid="message-template-modal"
        title={getModalTitle()}
        description={getModalDescription()}
        height={680}
        size="lg"
        shouldCloseOnEsc={!generateTemplateDetailsMutation.isPending && !isGenerating}
        shouldCloseOnClickOutside={!generateTemplateDetailsMutation.isPending && !isGenerating}
        onOpenChange={(state) => {
          onOpenChange(state);

          // Abort on-going request on close
          if (!state) {
            abortController.abort();
          }
        }}
        {...{ isOpened }}
      >
        <Modal.Body withPadding={modalState === TemplateSelectorModalState.BROWSE}>
          {modalState === TemplateSelectorModalState.BROWSE && (
            <>
              {templates.length > 0 && (
                <section className="flex flex-1 flex-col gap-space-md pb-space-md">
                  <SearchInput
                    name="search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t("page.admin-create-post.template-selector-modal.search-input.placeholder")}
                  />
                  {debouncedQuery && (
                    <Tiles columns={{ base: 1, container: { md: 2 } }} gap="md">
                      {searchedTemplates.length === 0 && (
                        <section className="col-span-full py-space-md">
                          <p className="text-center text-caption text-text-secondary">
                            {t("page.admin-create-post.template-selector-modal.no-results")}
                          </p>
                        </section>
                      )}
                      {searchedTemplates.length > 0 &&
                        searchedTemplates.map((template) => (
                          <TemplateSelectorOptionCard
                            key={template.id}
                            template={template}
                            onSelect={handleSelectPreviewTemplate}
                          />
                        ))}
                    </Tiles>
                  )}
                  {!debouncedQuery && (
                    <Tabs.Root
                      activeTab={activeTab}
                      onSelectTab={setActiveTab}
                      tabs={templateCategories.map((category) => ({
                        value: category,
                        label: getCategoryLabel(category),
                        "data-testid": `tab-${category}`,
                      }))}
                    >
                      {templateCategories.map((category) => (
                        <Tabs.Content key={category} value={category}>
                          <section className="grid grid-cols-1 gap-space-md sm:grid-cols-2">
                            {activeTab === "ALL" && (
                              <Card
                                as="button"
                                type="button"
                                density="spacious"
                                minHeight="media"
                                borderTone="secondary"
                                interactive
                                onClick={handleSelectNoTemplate}
                                data-testid="blank-template-btn"
                              >
                                <Stack gap="sm" align="center">
                                  <Edit05Icon size={40} />
                                  <h3 className="text-center text-heading-3">
                                    {t("page.admin-create-post.template-selector-modal.new-blank-post.title")}
                                  </h3>
                                  <p className="text-center text-body">
                                    {t("page.admin-create-post.template-selector-modal.new-blank-post.description")}
                                  </p>
                                </Stack>
                              </Card>
                            )}
                            {templateGroupByCategory[activeTab]
                              .toSorted((a, b) => a.name.localeCompare(b.name))
                              .map((currTemplate) => (
                                <TemplateSelectorOptionCard
                                  key={currTemplate.id}
                                  template={currTemplate}
                                  onSelect={handleSelectPreviewTemplate}
                                />
                              ))}
                          </section>
                        </Tabs.Content>
                      ))}
                    </Tabs.Root>
                  )}
                </section>
              )}
              {templates.length === 0 && (
                <section className="w-full py-space-md">
                  <p className="text-center text-caption text-text-secondary">
                    {t("page.admin-create-post.template-selector-modal.no-results")}
                  </p>
                </section>
              )}
            </>
          )}
          {modalState === TemplateSelectorModalState.PREVIEW && (
            <section className="flex flex-1 flex-col items-center justify-start gap-space-xl bg-background-secondary p-space-md @md:p-space-lg">
              {/* Preview content */}
              <section className="flex max-h-scroll-area-compact w-full max-w-sm flex-1 text-caption">
                <Card as="article" borderTone="secondary" scroll="content">
                  {generateTemplateDetailsMutation.isPending && (
                    <aside className="absolute inset-0 z-10 flex animate-in items-center justify-center rounded-xl bg-background-default/80 fade-in">
                      <FullSizeLoader size="sm" noDelay />
                    </aside>
                  )}
                  <h5 className="text-caption">
                    <b className="text-caption-emphasis">{t("page.admin-create-post.form.title")}: </b>
                    {templateDetails.title || "..."}
                  </h5>
                  {templateDetails.content && (
                    <section className="text-caption">
                      <MultiLineContent>{formatTemplateContent(templateDetails.content)}</MultiLineContent>
                    </section>
                  )}
                </Card>
              </section>

              <TemplateSelectorToneOfVoiceSelector
                selectedToneOfVoice={selectedToneOfVoice}
                onSelectToneOfVoice={handleChangeToneOfVoice}
                isDisabled={generateTemplateDetailsMutation.isPending || isGenerating}
              />
            </section>
          )}
        </Modal.Body>
        {modalState === TemplateSelectorModalState.PREVIEW && previewTemplate && (
          <Modal.Actions isSticky>
            <Button variant="outline" onClick={handleDeselectPreviewTemplate}>
              {t("common.action.back")}
            </Button>
            <Button
              data-testid="use-template-btn"
              onClick={handleSelectPreviewedTemplate}
              disabled={generateTemplateDetailsMutation.isPending || isGenerating}
            >
              {t("page.admin-create-post.template-selector-modal.btn.select-template")}
            </Button>
          </Modal.Actions>
        )}
      </Modal.Root>
      <ConfirmModal
        tone="primary"
        title={t("page.admin-create-post.template-overwrite-confirm-modal.title")}
        description={t("page.admin-create-post.template-overwrite-confirm-modal.description")}
        isOpened={isOverwriteConfirmModalOpened}
        onOpenChange={overwriteConfirmModalHandler.set}
        onReject={overwriteConfirmModalHandler.setFalse}
        rejectBtnProps={{
          text: t("common.action.cancel"),
        }}
        onResolve={handleSelectTemplateWithOverwrite}
        resolveBtnProps={{
          text: t("common.action.continue"),
        }}
        isLoading={generateTemplateDetailsMutation.isPending || isGenerating}
      />
    </>
  );
}

type TemplateSelectorToneOfVoiceSelectorProps = {
  selectedToneOfVoice: ToneOfVoice;
  onSelectToneOfVoice: (toneOfVoice: ToneOfVoice) => void;
  isDisabled: boolean;
};

function TemplateSelectorToneOfVoiceSelector({
  selectedToneOfVoice,
  onSelectToneOfVoice,
  isDisabled,
}: TemplateSelectorToneOfVoiceSelectorProps) {
  const { t } = useTranslation();

  const toneOfVoiceConfig: Record<
    ToneOfVoice,
    {
      label: string;
      description: string;
    }
  > = {
    professional: {
      label: t("page.admin-create-post.template-selector-modal.tone-of-voice.option.professional.label"),
      description: t("page.admin-create-post.template-selector-modal.tone-of-voice.option.professional.description"),
    },
    neutral: {
      label: t("page.admin-create-post.template-selector-modal.tone-of-voice.option.neutral.label"),
      description: t("page.admin-create-post.template-selector-modal.tone-of-voice.option.neutral.description"),
    },
    playful: {
      label: t("page.admin-create-post.template-selector-modal.tone-of-voice.option.playful.label"),
      description: t("page.admin-create-post.template-selector-modal.tone-of-voice.option.playful.description"),
    },
  };

  return (
    <footer className="mt-auto flex flex-col justify-center gap-space-md">
      <Stack align="center">
        <h3 className="text-center text-heading-3">
          {t("page.admin-create-post.template-selector-modal.tone-of-voice.title")}
        </h3>
        <p className="text-center text-body">
          {t("page.admin-create-post.template-selector-modal.tone-of-voice.description")}
        </p>
      </Stack>
      <FormRadioCardGroup>
        {Object.keys(toneOfVoiceConfig).map((currToneOfVoice) => {
          const currLabel = toneOfVoiceConfig[currToneOfVoice as ToneOfVoice].label;
          const currDescription = toneOfVoiceConfig[currToneOfVoice as ToneOfVoice].description;

          return (
            <RadioCard
              key={currToneOfVoice}
              name="tone-of-voice"
              value={currToneOfVoice}
              label={currLabel}
              disabled={isDisabled}
              checked={selectedToneOfVoice === currToneOfVoice}
              description={currDescription}
              onChange={() => onSelectToneOfVoice(currToneOfVoice as ToneOfVoice)}
            />
          );
        })}
      </FormRadioCardGroup>
    </footer>
  );
}

type TemplateSelectorOptionCardProps = {
  template: PostTemplateDto;
  onSelect: (template: PostTemplateDto) => void;
};

const TemplateSelectorOptionCard = ({ template, onSelect }: TemplateSelectorOptionCardProps) => {
  const getCategoryIcon = (category: string) => {
    switch (category) {
      case "COMMUNITY":
        return <Users01Icon />;
      case "MAINTENANCE":
        return <Tool01Icon />;
      case "AMENITIES":
        return <Building04Icon />;
      default:
        return <Asterisk01Icon />;
    }
  };

  return (
    <Card
      as="button"
      type="button"
      data-testid="preview-template-btn"
      density="spacious"
      minHeight="media"
      borderTone="secondary"
      interactive
      onClick={() => onSelect(template)}
    >
      <Inline gap="sm" align="start" wrap={false}>
        <span className="flex h-toggle-button-height-sm items-center">
          {cloneElement(getCategoryIcon(template.category), { size: 24 })}
        </span>
        <Stack gap="sm" align="start">
          <h4 className="line-clamp-2 text-left text-heading-3">{template.name}</h4>
          {/* Fixed height to make sure each card is of the same height */}
          <p className="line-clamp-2 min-h-control-xl w-full text-left text-body text-text-secondary @sm:line-clamp-5">
            {template.description}
          </p>
        </Stack>
      </Inline>
    </Card>
  );
};
